import { useSuspenseQuery, useQuery } from '@tanstack/react-query'
import { signOut } from 'next-auth/react'
import { supplierPortalPath } from '@omnea-app/fe-api-client/supplierPortalPath'
import { useAuthenticatedUser, useIsSupplierRole } from '@omnea-app/fe-auth/UserSessionProvider'
import { isNotFoundApiError } from '@omnea-app/packages/universal/error-handling/isApiErrorOfType'
import { UsersHttpClient } from '@omnea-app/users/usersHttpClient'

// Takes the signed-in user's id only to key the cache and to wait for a session: /me reads the user from the token.
const useGetCurrentUserQuery = (userId?: string) => {
  const { getToken } = useAuthenticatedUser()
  const isSupplier = useIsSupplierRole()
  return {
    queryKey: ['getCurrentUser', userId],
    queryFn: async () => {
      if (!userId) return null

      try {
        const token = await getToken()
        const apiClient = new UsersHttpClient({ omneaJwt: token })
        const response = await apiClient.GET(supplierPortalPath('/me', isSupplier))

        if ('error' in response && !('data' in response)) {
          throw response.error instanceof Error ? response.error : new Error(JSON.stringify(response.error))
        }
        return response.data
      } catch (error) {
        if (isNotFoundApiError(error)) {
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
            await signOut({ callbackUrl: '/login' })
          }
          return null
        }

        throw error
      }
    },
  }
}

export const useGetCurrentUserSuspense = (userId?: string) => {
  return useSuspenseQuery({
    ...useGetCurrentUserQuery(userId),
    initialData: !userId ? null : undefined, // Prevents component from suspending if there is no signed-in user
    retry: (_, error) => !isNotFoundApiError(error),
  })
}

export const useGetCurrentUser = (userId?: string) => {
  return useQuery({
    ...useGetCurrentUserQuery(userId),
  })
}
